/**
 * Client read model for employee risk — mirrors server riskStateService output.
 * Never scores risk here; only normalizes API payloads.
 */
import {
  buildEmployeeRiskState,
  turnoverViewFromRiskState,
} from '@stavyn/shared/employeeRiskState';

export { buildEmployeeRiskState, turnoverViewFromRiskState };

/**
 * Canonical risk state from an employee API row.
 * @param {object|null|undefined} employee
 * @returns {object}
 */
export function riskStateFromEmployee(employee) {
  if (employee?.riskState && typeof employee.riskState === 'object') {
    return employee.riskState;
  }
  return buildEmployeeRiskState(employee, null);
}

const TURNOVER_EXTENSION_KEYS = [
  'similarCases',
  'matchedPatterns',
  'factorBreakdown',
  'excludedFactors',
  'temporal',
  'topContributors',
];

function mergeTurnoverExtensions(base, extension) {
  if (!extension || typeof extension !== 'object') return base;
  if (!base || typeof base !== 'object') return extension;
  const out = { ...base };
  for (const key of TURNOVER_EXTENSION_KEYS) {
    const value = extension[key];
    if (key === 'temporal') {
      if (value && typeof value === 'object') out.temporal = value;
      continue;
    }
    if (Array.isArray(value) && value.length > 0) {
      out[key] = value;
    }
  }
  return out;
}

/**
 * Turnover-shaped view for RiskScore / legacy components — derived from riskState only.
 * @param {object|null|undefined} employee
 * @returns {object|null}
 */
export function turnoverFromEmployee(employee) {
  const extension = employee?.turnover;
  const state = riskStateFromEmployee(employee);
  const view = turnoverViewFromRiskState(state);
  if (view) return mergeTurnoverExtensions(view, extension);
  const p = employee?.risk_score_payload;
  if (p && typeof p === 'object') return p;
  return extension && typeof extension === 'object' ? extension : null;
}

/**
 * Object shaped for getPrimaryRiskDisplay / getSecondaryRiskDisplay helpers.
 * @param {object|null|undefined} employee
 * @returns {object|null}
 */
export function riskDisplaySourceFromEmployee(employee) {
  return turnoverFromEmployee(employee);
}
